/* Render every cart surface from the same authoritative Shopify Ajax cart. */
(() => {
  'use strict';
  if (window.NiralaCartRenderer) return;

  const groupOf = item => String(item?.properties?._nirala_addon_group || '');
  const parentKey = item => String(item?.parent_relationship?.parent_key || item?.parent_relationship?.parent?.key || '');
  const isAddon = item => String(item?.properties?._nirala_addon) === 'true';
  const quantityOf = item => Math.max(0, Math.floor(Number(item.quantity) || 0));
  const text = value => value == null ? '' : String(value);
  const reviewMessage = 'The item and its toppings could not be updated together. Remove the main item with its toppings and add it again before checkout.';
  const renderedSurfaces = new WeakMap();
  const pendingFocus = new Map();

  function captureFocus(surface) {
    const active = document.activeElement;
    if (!surface?.contains(active)) return;
    let selector = '';
    if (active.matches('[data-cart-step="1"]')) selector = '[data-cart-step="1"]';
    else if (active.matches('[data-cart-step="-1"]')) selector = '[data-cart-step="-1"]';
    else if (active.matches('[data-cart-quantity]')) selector = '[data-cart-quantity]:not([type="hidden"])';
    else if (active.matches('[data-cart-remove]')) selector = '[data-cart-remove]';
    else if (active.matches('[name="note"]')) selector = '[name="note"]';
    if (!selector) return;
    const line = active.closest('[data-cart-line-key]');
    pendingFocus.set(surface, {
      original: active, selector, key: line?.dataset.cartLineKey,
      index: line ? [...surface.querySelectorAll('[data-cart-line-key]')].indexOf(line) : -1,
      start: active.selectionStart, end: active.selectionEnd
    });
  }

  function restoreFocus() {
    if (window.NiralaCartControls?.isBusy?.()) return;
    for (const [surface, saved] of pendingFocus) {
      pendingFocus.delete(surface);
      if (!surface.isConnected) continue;
      const active = document.activeElement;
      // Do not steal focus if the customer deliberately moved to another control.
      if (active && active !== document.body && active !== saved.original && active.isConnected) continue;
      let target;
      if (saved.key) {
        const lines = [...surface.querySelectorAll('[data-cart-line-key]')];
        const same = lines.find(line => line.dataset.cartLineKey === saved.key);
        const line = same || lines[Math.min(saved.index, lines.length - 1)];
        target = same?.querySelector(saved.selector)
          || line?.querySelector('[data-cart-quantity]:not([type="hidden"]), [data-cart-step="1"], [data-cart-remove]');
      } else target = surface.querySelector(saved.selector);
      if (!target || target.disabled || target.hidden) target = surface.querySelector('.np-empty-state a')
        || surface.closest('[data-cart-drawer]')?.querySelector('[data-cart-close]');
      if (!target || target.disabled || target.hidden) continue;
      target.focus({ preventScroll: true });
      if (typeof target.setSelectionRange === 'function' && saved.start != null) {
        try { target.setSelectionRange(saved.start, saved.end); } catch (_) { /* Number inputs have no text selection API. */ }
      }
    }
  }

  function fingerprint(cart) {
    // Delivery summaries have their own updater. Excluding attributes preserves a
    // customer's focused form while a location choice is saved in the background.
    return JSON.stringify([
      cart.item_count, cart.total_price, cart.items_subtotal_price ?? cart.total_price ?? 0, cart.total_discount ?? 0,
      cart.taxes_included === true, cart.requires_shipping !== false, cart.currency?.iso_code || cart.currency, text(cart.note),
      cart.cart_level_discount_applications || [],
      cart.items.map(item => [
        item.key, quantityOf(item), item.product_title || item.title, item.variant_title,
        item.url, item.url_to_remove, item.image, item.featured_image,
        item.final_line_price ?? item.line_price, item.original_line_price,
        Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
        parentKey(item), item.instructions || {}, item.selling_plan_allocation || {},
        item.line_level_discount_allocations || [], item.discounts || []
      ])
    ]);
  }

  function currentShell(surface, cart) {
    return surface.querySelector(cart.items.length ? '[data-cart-form]' : '.np-empty-state');
  }

  function lineIdentitiesMatch(surface, cart) {
    const lines = [...surface.querySelectorAll('[data-cart-line-key]')];
    if (lines.length !== cart.items.length) return false;
    return lines.every((line, index) => {
      const item = cart.items[index];
      const input = line.querySelector('[data-cart-quantity]');
      return line.dataset.cartLineKey === text(item.key)
        && Number(input?.dataset.cartOriginal) === quantityOf(item)
        && text(line.dataset.cartParentKey) === parentKey(item)
        && text(line.dataset.cartAddonGroup) === groupOf(item)
        && line.querySelector('.np-cart-line__details > a strong')?.textContent === text(item.product_title || item.title);
    });
  }

  function matchesInitialSnapshot(surface, cart) {
    let snapshot;
    try { snapshot = JSON.parse(surface.querySelector('[data-cart-snapshot]')?.textContent || 'null'); }
    catch (_) { return false; }
    if (!snapshot || !Array.isArray(snapshot.items) || !currentShell(surface, cart)) return false;
    if (Number(snapshot.item_count) !== Number(cart.item_count) || Number(snapshot.total_price) !== Number(cart.total_price)) return false;
    if (Number(snapshot.items_subtotal_price ?? snapshot.total_price ?? 0) !== Number(cart.items_subtotal_price ?? cart.total_price ?? 0)
      || Number(snapshot.total_discount ?? 0) !== Number(cart.total_discount ?? 0)
      || (snapshot.taxes_included === true) !== (cart.taxes_included === true)
      || (snapshot.requires_shipping !== false) !== (cart.requires_shipping !== false)) return false;
    if (snapshot.items.length !== cart.items.length || !lineIdentitiesMatch(surface, cart)) return false;
    const note = surface.querySelector('[name="note"]');
    if (note && note.defaultValue !== text(cart.note)) return false;
    if (cart.items.some(item => item.selling_plan_allocation?.selling_plan?.name)) return false;
    return snapshot.items.every((item, index) => item.key === cart.items[index].key
      && Number(item.quantity) === quantityOf(cart.items[index])
      && Number(item.final_line_price) === Number(cart.items[index].final_line_price ?? cart.items[index].line_price));
  }

  function safeURL(value, fallback = '#') {
    if (!value || typeof value !== 'string') return fallback;
    try {
      const url = new URL(value, window.location.href);
      return /^https?:$/.test(url.protocol) ? url.href : fallback;
    } catch (_) { return fallback; }
  }

  function money(value, currency) {
    if (typeof window.Nirala?.formatMoney === 'function') return window.Nirala.formatMoney(Number(value) || 0, currency);
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: currency || 'PKR' }).format((Number(value) || 0) / 100); }
    catch (_) { return ((Number(value) || 0) / 100).toFixed(2); }
  }

  function clone(id) {
    return document.getElementById(id)?.content?.firstElementChild?.cloneNode(true) || null;
  }

  function mismatch(items) {
    return items.some(item => {
      const group = groupOf(item);
      if (!group) return false;
      if (isAddon(item)) {
        const parent = items.find(row => row.key === parentKey(item));
        return !parent || groupOf(parent) !== group || quantityOf(parent) !== quantityOf(item);
      }
      const expected = Number(item.properties?._nirala_addon_count || 0);
      return expected > 0 && items.filter(child => parentKey(child) === item.key && groupOf(child) === group && isAddon(child)).length !== expected;
    });
  }

  function removeURL(item) {
    const root = window.Nirala?.config?.root || '/';
    const base = safeURL(root.endsWith('/') ? root : `${root}/`, window.location.origin + '/');
    const url = new URL('cart/change', base);
    url.searchParams.set('id', item.key);
    url.searchParams.set('quantity', '0');
    return safeURL(item.url_to_remove, url.href);
  }

  function renderLine(item, cart) {
    const line = clone('NiralaCartLineTemplate');
    if (!line) return null;
    const key = text(item.key);
    const title = text(item.product_title || item.title);
    const group = groupOf(item);
    const parent = cart.items.find(row => row.key === parentKey(item));
    const isChild = Boolean(parentKey(item));
    const managedChild = Boolean(group && isAddon(item));
    const hasChildren = !isChild && Boolean(group) && (Number(item.properties?._nirala_addon_count || 0) > 0 || cart.items.some(child => parentKey(child) === key && groupOf(child) === group));
    const quantity = quantityOf(item);
    const details = line.querySelector('.np-cart-line__details');
    const quantityRow = line.querySelector('.np-cart-quantity-row');
    const priceRow = line.querySelector('.np-cart-line__price');
    const addDetail = (value, className) => {
      if (!value) return;
      const detail = document.createElement('small');
      detail.textContent = value;
      if (className) detail.className = className;
      details.insertBefore(detail, priceRow);
    };

    line.dataset.cartLineKey = key;
    if (isChild) {
      line.classList.add('np-cart-line--child');
      line.dataset.cartParentKey = parentKey(item);
    }
    if (group) line.dataset.cartAddonGroup = group;
    line.querySelectorAll('[data-cart-product-link]').forEach(link => { link.href = safeURL(item.url); });
    line.querySelector('[data-cart-product-title]').textContent = title;
    const imageURL = safeURL(typeof item.image === 'string' ? item.image : item.image?.url || item.image?.src || item.featured_image?.url || item.featured_image?.src, '');
    if (imageURL) {
      const img = document.createElement('img');
      img.src = imageURL;
      img.alt = text(item.featured_image?.alt || title);
      img.loading = 'lazy';
      img.width = 220;
      img.height = 220;
      line.querySelector('.np-cart-line__image').replaceChildren(img);
    }
    if (parent) addDetail(`For ${text(parent.product_title || parent.title)}`, 'np-cart-parent-label');
    const variantTitle = text(item.variant_title);
    if (variantTitle && variantTitle !== 'Default Title') addDetail(variantTitle);
    Object.entries(item.properties || {}).forEach(([name, value]) => {
      if (!name.startsWith('_') && value != null && text(value) !== '' && !(isChild && name === 'For product')) addDetail(`${name}: ${text(value)}`);
    });
    const plan = item.selling_plan_allocation?.selling_plan;
    if (plan?.name) addDetail(text(plan.name), 'np-cart-selling-plan');
    const discounts = item.line_level_discount_allocations?.length ? item.line_level_discount_allocations : item.discounts || [];
    discounts.forEach(discount => addDetail(`${text(discount.discount_application?.title || discount.title || 'Discount').replace(/^Nirala\s*\[[0-9a-f-]{36}\]\s*/i, '') || 'Midnight Deals'} (−${money(discount.amount, cart.currency)})`));

    let input = line.querySelector('[data-cart-quantity]');
    if (managedChild || item.instructions?.can_update_quantity === false) {
      const display = document.createElement('span');
      display.dataset.cartQuantityDisplay = '';
      display.textContent = String(quantity);
      input = document.createElement('input');
      input.type = 'hidden';
      input.dataset.cartQuantity = '';
      if (managedChild) input.dataset.cartChild = '';
      if (isChild) input.dataset.cartParentKey = parentKey(item);
      line.querySelector('.np-cart-adjust').replaceWith(display, input);
    } else {
      input.max = String(Math.max(99, quantity));
      if (item.instructions?.can_remove === false) input.min = '1';
      input.setAttribute('aria-label', `${input.getAttribute('aria-label')} — ${title}`);
      line.querySelectorAll('[data-cart-step]').forEach(button => {
        button.setAttribute('aria-label', `${button.getAttribute('aria-label')} — ${title}`);
        if (item.instructions?.can_remove === false && quantity <= 1 && button.dataset.cartStep === '-1') button.disabled = true;
      });
      if (hasChildren) { input.dataset.cartAddonParent = group; input.readOnly = true; }
    }
    input.name = `updates[${key}]`;
    input.value = String(quantity);
    input.defaultValue = String(quantity);
    input.dataset.cartKey = key;
    input.dataset.cartOriginal = String(quantity);

    const remove = line.querySelector('[data-cart-remove]');
    remove.href = removeURL(item);
    if (managedChild) {
      const help = document.createElement('small');
      help.className = 'np-cart-addon-help';
      help.textContent = 'Quantity follows the main item. To change toppings, remove the main item and add it again.';
      line.querySelector('.np-cart-line__actions').before(help);
      if (!isChild && item.instructions?.can_remove !== false) {
        remove.textContent = 'Remove unattached topping';
        // Preserve the native recovery link: managed children cannot be edited alone.
        remove.removeAttribute('data-cart-remove');
      } else remove.remove();
    } else if (item.instructions?.can_remove === false) remove.remove();
    else if (hasChildren) remove.textContent = 'Remove with toppings';

    const finalPrice = Number(item.final_line_price ?? item.line_price ?? 0);
    const originalPrice = Number(item.original_line_price ?? finalPrice);
    const total = line.querySelector('[data-cart-line-total]');
    total.textContent = money(finalPrice, cart.currency);
    if (originalPrice > finalPrice && originalPrice > 0) {
      const badge = document.createElement('span');
      badge.className = 'np-cart-offer-badge';
      badge.textContent = `${Math.round((originalPrice-finalPrice)/originalPrice*1000)/10}% OFF`;
      total.closest('.np-cart-line__price').before(badge);
      const original = document.createElement('s');
      original.textContent = money(originalPrice, cart.currency);
      total.before(original);
    }
    return line;
  }

  function captureDrafts(surface) {
    if (window.NiralaCartControls?.isBusy?.()) return null;
    const form = surface.querySelector('[data-cart-form]');
    if (!form || form.getAttribute('aria-busy') === 'true') return null;
    const note = form.querySelector('[name="note"]');
    const quantities = new Map();
    form.querySelectorAll('[data-cart-quantity]:not([data-cart-child])').forEach(input => {
      if (input.value !== input.dataset.cartOriginal) quantities.set(input.dataset.cartKey, input.value);
    });
    return { note: note && note.value !== note.defaultValue ? note.value : null, quantities };
  }

  function renderForm(cart, drafts) {
    const form = clone('NiralaCartFormTemplate');
    if (!form) return null;
    const lines = form.querySelector('.np-cart-lines');
    for (const item of cart.items) {
      const line = renderLine(item, cart);
      if (!line) return null;
      lines.appendChild(line);
    }
    const note = form.querySelector('[name="note"]');
    if (note) { note.defaultValue = text(cart.note); note.value = drafts?.note ?? text(cart.note); }
    if (drafts) form.querySelectorAll('[data-cart-quantity]:not([data-cart-child])').forEach(input => {
      if (drafts.quantities.has(input.dataset.cartKey)) input.value = drafts.quantities.get(input.dataset.cartKey);
    });
    const attrs = cart.attributes || {};
    form.querySelector('[data-cart-delivery-title]').textContent = attrs['Order type'] === 'Pickup' ? 'Pickup address' : 'Delivery address';
    [['street', 'Delivery address'], ['area', 'Delivery area'], ['city', 'Delivery city']].forEach(([field, attr]) => {
      const node = form.querySelector(`[data-cart-delivery-${field}]`);
      node.textContent = text(attrs[attr]);
      node.hidden = !text(attrs[attr]);
    });
    form.querySelector('[data-cart-delivery-empty]').hidden = Boolean(text(attrs['Delivery address']));
    const discounts = form.querySelector('[data-cart-discounts]');
    (cart.cart_level_discount_applications || []).forEach(discount => {
      const row = document.createElement('div');
      row.className = 'np-cart-summary__row np-cart-discount';
      const label = document.createElement('span');
      label.textContent = text(discount.title).replace(/^Nirala\s*\[[0-9a-f-]{36}\]\s*/i, '') || 'Midnight Deals';
      const amount = document.createElement('strong');
      amount.textContent = `−${money(discount.total_allocated_amount, cart.currency)}`;
      row.append(label, amount);
      discounts.append(row);
    });
    const discountTotal = (cart.cart_level_discount_applications || []).reduce((sum, discount) => sum + (Number(discount.total_allocated_amount) || 0), 0);
    // Shopify subtotal includes line discounts, but not order-level discounts.
    const subtotal = cart.items_subtotal_price == null ? (Number(cart.total_price) || 0) + discountTotal : cart.items_subtotal_price;
    form.querySelector('[data-cart-items-subtotal]').textContent = money(subtotal, cart.currency);
    form.querySelector('[data-cart-total]').textContent = money(cart.total_price, cart.currency);
    const shipping = cart.requires_shipping !== false;
    const taxesIncluded = cart.taxes_included === true;
    const delivery = form.querySelector('[data-cart-delivery-charge]');
    if (delivery) delivery.textContent = shipping ? 'Calculated at checkout' : 'Not required';
    const tax = form.querySelector('[data-cart-tax-status]');
    if (tax) tax.textContent = taxesIncluded ? 'Included in prices' : 'Calculated at checkout';
    form.querySelector('[data-cart-total-qualifier]').textContent = shipping
      ? (taxesIncluded ? '(before delivery)' : '(before delivery & tax)')
      : (taxesIncluded ? '' : '(before tax)');
    form.querySelector('[data-cart-checkout-note]').textContent = shipping
      ? (taxesIncluded ? 'Final delivery charges are calculated at checkout.' : 'Final delivery charges and any taxes are calculated at checkout.')
      : (taxesIncluded ? 'Taxes are included in prices.' : 'Any taxes are calculated at checkout.');
    const savings = form.querySelector('[data-cart-savings]');
    if (savings) {
      const amount = Math.max(0, Number(cart.total_discount) || 0);
      savings.hidden = amount === 0;
      savings.querySelector('[data-cart-savings-amount]').textContent = money(amount, cart.currency);
    }
    const blocked = mismatch(cart.items);
    form.dataset.cartAddonMismatch = String(blocked);
    if (blocked) {
      const error = form.querySelector('[data-cart-error]');
      error.textContent = reviewMessage;
      error.hidden = false;
      const button = form.querySelector('[name="checkout"]');
      button.disabled = true;
      button.dataset.addonBlocked = 'true';
    }
    window.Nirala?.syncMinimumOrder(cart, form);
    return form;
  }

  function render(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    const signature = fingerprint(cart);
    const replacements = [];
    const unchanged = [];
    for (const surface of document.querySelectorAll('[data-cart-content]')) {
      if (surface.matches('[data-checkout-pending="true"]') || surface.querySelector('[data-checkout-pending="true"]')) continue;
      const previous = renderedSurfaces.get(surface);
      const shell = currentShell(surface, cart);
      if ((previous?.signature === signature && previous.shell === shell && shell && lineIdentitiesMatch(surface, cart))
        || (!previous && matchesInitialSnapshot(surface, cart))) {
        unchanged.push([surface, shell]);
        continue;
      }
      const replacement = cart.items.length ? renderForm(cart, captureDrafts(surface)) : clone('NiralaCartEmptyTemplate');
      if (!replacement) return false;
      captureFocus(surface);
      replacements.push([surface, replacement, surface.querySelector('[data-cart-scrollbody]')?.scrollTop || 0]);
    }
    for (const [surface, replacement, scrollTop] of replacements) {
      surface.replaceChildren(replacement);
      const scrollbody = surface.querySelector('[data-cart-scrollbody]');
      if (scrollbody) scrollbody.scrollTop = scrollTop;
      renderedSurfaces.set(surface, { signature, shell: replacement });
    }
    for (const [surface, shell] of unchanged) renderedSurfaces.set(surface, { signature, shell });
    queueMicrotask(restoreFocus);
    return true;
  }

  window.NiralaCartRenderer = { render, captureFocus, restoreFocus };
})();
